import { Fragment } from 'react';
import Link from 'next/link';
import { Plus, Search } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { NAV_ICONS } from './sidebar/components/nav-icons';
import { useSidebarNav, type NavId, type SidebarNavItem } from './sidebar/hooks/use-sidebar-nav';
import { IconButton } from './ui';
import styles from './NavRail.module.css';

// The WIDE rail's primary nav. Which destinations exist, where they go and what a
// click does lives in useSidebarNav — the mobile drawer renders the same items with
// its own markup (components/symphony/sidebar/mobile/MobileNav.tsx).
export type { NavId };
export { ConnectorsIcon } from './sidebar/components/nav-icons';

export function NavRail({
  current,
  collapsed = false,
  onClose,
  onOpenSearch,
}: {
  current?: NavId;
  collapsed?: boolean;
  onClose?: () => void;
  onOpenSearch?: () => void;
}) {
  const { t } = useTranslation();
  const { items, newChat } = useSidebarNav({ current, onClose });

  return (
    <nav
      aria-label={t('nav.primary')}
      style={{
        // Constant top padding both states: the collapse keeps the icons put (no
        // vertical pop), so the logo→first-icon gap can't jump when toggling.
        paddingTop: 24,
        paddingBottom: collapsed ? 12 : 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {items.map((item, i) => (
        <Fragment key={item.id}>
          <div className={styles.destinationRow}>
            <NavItem item={item} collapsed={collapsed} />
            {item.id === 'chats' && newChat.visible && !collapsed && (
              <IconButton size="small" disabled={newChat.disabled} onClick={newChat.onClick}
                aria-label={newChat.label} title={newChat.label}>
                <Plus size={16} aria-hidden />
              </IconButton>
            )}
          </div>
          {/* Search — collapsed rail only: appears as a nav-row icon between Chat and Home */}
          {i === 0 && collapsed && onOpenSearch && (
            <button
              type="button"
              onClick={onOpenSearch}
              aria-label={t('nav.search')}
              title={t('nav.search')}
              className="orion-nav-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                padding: '6px 0',
                border: 'none',
                borderRadius: 'var(--ds-radius-8)',
                cursor: 'pointer',
                color: 'var(--ds-icon-primary)',
                fontFamily: 'inherit',
              }}
            >
              <span aria-hidden className="orion-nav-icon" style={{ width: 24, height: 24, flexShrink: 0, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Search size={16} aria-hidden />
              </span>
            </button>
          )}
        </Fragment>
      ))}
    </nav>
  );
}

function NavItem({ item, collapsed }: { item: SidebarNavItem; collapsed: boolean }) {
  return (
    <Link
      href={item.route}
      as={item.path}
      aria-current={item.isActive ? 'page' : undefined}
      onClick={item.onClick}
      title={collapsed ? item.label : undefined}
      className="orion-nav-item"
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        width: collapsed ? 36 : undefined,
        minWidth: 0,
        flex: collapsed ? undefined : 1,
        margin: collapsed ? undefined : 0,
        gap: collapsed ? 0 : 10,
        padding: collapsed ? '6px 0' : '5px 8px',
        borderRadius: 'var(--ds-radius-8)',
        textDecoration: 'none',
        color: 'var(--ds-text-primary)',
        fontSize: 14,
        fontWeight: item.isActive ? 'var(--sym-weight-semibold)' : 'var(--sym-weight-medium)',
        transition: 'background-color 0.15s ease, color 0.15s ease',
      }}
    >
      <span
        aria-hidden
        className="orion-nav-icon"
        style={{
          width: 24,
          height: 24,
          flexShrink: 0,
          borderRadius: 7,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--ds-icon-primary)',
        }}
      >
        {NAV_ICONS[item.id]}
      </span>
      {!collapsed && <span className="orion-rail-label">{item.label}</span>}
    </Link>
  );
}
